import { useTranslation } from 'react-i18next'
import { CheckCircle2 } from 'lucide-react'
import type { FeatureWithActions, FeaturePriority } from '@/shared/types'
import { cn } from '@/shared/lib/utils'

interface FeatureCardProps {
  feature: FeatureWithActions
  onClick?: () => void
}

function PriorityBadge({ priority }: { priority: FeaturePriority }) {
  const { t } = useTranslation()

  const styles: Record<FeaturePriority, string> = {
    high: 'bg-red-100 text-red-700',
    medium: 'bg-amber-100 text-amber-700',
    low: 'bg-slate-100 text-slate-600',
  }

  return (
    <span className={cn('rounded px-2 py-0.5 text-xs font-medium', styles[priority])}>
      {t(`kanban.priority.${priority}`)}
    </span>
  )
}

function DoneBadge() {
  const { t } = useTranslation()

  return (
    <span className="flex items-center gap-1 rounded bg-green-100 px-2 py-0.5 text-[11px] font-semibold text-green-600">
      <CheckCircle2 className="h-3.5 w-3.5" />
      {t('kanban.columns.done')}
    </span>
  )
}

function ActionBadge({ pendingValidation, testing }: { pendingValidation: number; testing: number }) {
  const { t } = useTranslation()

  // Determine which action to show (prioritize pending validation)
  if (pendingValidation > 0) {
    return (
      <span className="rounded border border-accent bg-orange-50 px-2 py-0.5 text-[11px] font-semibold text-accent">
        {t('kanban.usToValidate', { count: pendingValidation })}
      </span>
    )
  }

  if (testing > 0) {
    return (
      <span className="rounded border border-accent bg-orange-50 px-2 py-0.5 text-[11px] font-semibold text-accent">
        {t('kanban.usToTest', { count: testing })}
      </span>
    )
  }

  return null
}

function USCountBadge({ count }: { count: number }) {
  if (count === 0) return null

  return (
    <span className="rounded bg-background-secondary px-2 py-0.5 text-[11px] font-medium text-foreground-muted">
      {count} US
    </span>
  )
}

export function FeatureCard({ feature, onClick }: FeatureCardProps) {
  const { actionCounts } = feature
  const isDone = feature.kanban_column === 'done'
  const hasAction = !isDone && (actionCounts.pendingValidation > 0 || actionCounts.testing > 0)

  return (
    <button
      onClick={onClick}
      className={cn(
        'flex w-full flex-col gap-3 rounded-lg border bg-white p-4 text-left',
        'transition-all hover:border-primary/20 hover:shadow-sm',
        hasAction ? 'border-accent' : 'border-border',
        isDone && 'opacity-70'
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        {isDone ? <DoneBadge /> : <PriorityBadge priority={feature.priority} />}
        <USCountBadge count={actionCounts.totalUS} />
      </div>

      {/* Title */}
      <h4 className="text-sm font-semibold text-foreground">
        {feature.name}
      </h4>

      {/* Description - hide for Done features */}
      {!isDone && feature.objective && (
        <p className="line-clamp-2 text-xs text-foreground-muted">
          {feature.objective}
        </p>
      )}

      {/* Action indicator - not shown for Done features */}
      {hasAction && (
        <div className="pt-1">
          <ActionBadge
            pendingValidation={actionCounts.pendingValidation}
            testing={actionCounts.testing}
          />
        </div>
      )}
    </button>
  )
}
